import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { AcuStorageService } from './acustorage.service';
import { DiagnosticSheetPeriod, DiagnosticSheetStartResponse, DiagnosticSheetStatus } from '../pages/jira/consuntivo/diagnostic-sheet';
import { XmlDriveKind, XmlDriveStartResponse, XmlDriveStatus } from '../pages/jira/consuntivo/acuplace-xml-drive';
import { BookDriveName, BookDriveStartResponse, BookDriveStatus } from '../pages/jira/book/book-drive';

export interface BookRequest {
  monthYear?: string,
  companyUnits?: Array<string>
}

export enum JarvisEndpoint {
  REPORT_DIAGNOSTICO_SHEET = "/report/diagnostic/sheet",
  REPORT_CONSUNTIVO = "/report/consuntivo/xml",
  REPORT_CONSUNTIVO_ACUPLACE_DRIVE = "/report/consuntivo/xml-acuplace/drive",
  REPORT_CONSUNTIVO_DRIVE = "/report/consuntivo/xml/drive",
  BOOK_ABSENCE = "/report/book/absence",
  BOOK_EXPENSE = "/report/book/expenseClaim",
  BOOK_TICKET = "/report/book/ticket",
  BOOK_DRIVE = "/report/book/drive",
  FORECAST_CONSUNTIVO_MENSILE_CURRENT = "/forecast/mensile/current",
  FORECAST_CONSUNTIVO_MENSILE_LAST = "/forecast/mensile/last",
  FORECAST_CONSUNTIVO_ANNUALE = "/forecast/mensile/year",
  FORECAST_PREVISION = "/forecast/prevision",
  FORECAST_PREVISION_PROJECT = "/forecast/project",
  FORECAST_PREVISION_PROJECT_SENIORITY_ALL = "/forecast/project-by-filter",
  FORECAST_ABSENCE = "/forecast/absence",
  EXPENSE_GENERATE_PDF = "/expense/generate"
}

const XML_DRIVE_ENDPOINT: Record<XmlDriveKind, JarvisEndpoint> = {
  tradizionale: JarvisEndpoint.REPORT_CONSUNTIVO_DRIVE,
  acuplace: JarvisEndpoint.REPORT_CONSUNTIVO_ACUPLACE_DRIVE,
};


@Injectable({
  providedIn: 'root'
})
export class JarvisApiService {

  private readonly baseUrl: string;
  private readonly endpointUser: string = "/user/email";
  private readonly endpointProvisioning: string = "/user/provision";

  private getHeaders(): HttpHeaders {
    const jwtToken = this.acuStorage.getJwtToken();
    return new HttpHeaders({ 'Authorization': `Bearer ${jwtToken}` });
  }

  constructor(private http: HttpClient, private acuStorage: AcuStorageService) {
    this.baseUrl = environment.jarvisApiUrl;
  }

  async getUserByEmail(email: string) {

    const requestOptions = { headers: this.getHeaders() };


    return await this.http.get<any>(
      this.baseUrl.concat(this.endpointUser).concat("/").concat(email), requestOptions
    ).toPromise();

  }

  /** Avvia in Jarvis il ricalcolo del diagnostico aziendale, che aggiorna la tab del periodo sullo sheet. */
  startDiagnosticSheetExport(period: DiagnosticSheetPeriod) {

    const requestOptions = { headers: this.getHeaders() };

    return this.http.post<DiagnosticSheetStartResponse>(`${this.baseUrl}${JarvisEndpoint.REPORT_DIAGNOSTICO_SHEET}/${period}`, {}, requestOptions);

  }

  getDiagnosticSheetExportStatus() {

    const requestOptions = { headers: this.getHeaders() };

    return this.http.get<DiagnosticSheetStatus[]>(`${this.baseUrl}${JarvisEndpoint.REPORT_DIAGNOSTICO_SHEET}/status`, requestOptions);

  }

  downloadXmlMensile(startDate: string, endDate: string) {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.REPORT_CONSUNTIVO}/${startDate}/${endDate}`, requestOptions);

  }

  /** Avvia in Jarvis la generazione dell'XML presenze del mese ("YYYY-MM"), che viene caricato sul Drive condiviso. */
  startXmlDriveExport(kind: XmlDriveKind, yearMonth: string) {

    const requestOptions = { headers: this.getHeaders() };

    return this.http.post<XmlDriveStartResponse>(`${this.baseUrl}${XML_DRIVE_ENDPOINT[kind]}/${yearMonth}`, {}, requestOptions);

  }

  getXmlDriveExportStatus(kind: XmlDriveKind, yearMonth: string) {

    const requestOptions = { headers: this.getHeaders() };

    return this.http.get<XmlDriveStatus>(`${this.baseUrl}${XML_DRIVE_ENDPOINT[kind]}/${yearMonth}/status`, requestOptions);

  }

  downloadBookAbsence(bookRequest: BookRequest) {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    const body = bookRequest;

    return this.http.post<Blob>(`${this.baseUrl}${JarvisEndpoint.BOOK_ABSENCE}`, body, requestOptions);

  }

  downloadBookExpenseClaim(bookRequest: BookRequest) {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    const body = bookRequest;

    return this.http.post<Blob>(`${this.baseUrl}${JarvisEndpoint.BOOK_EXPENSE}`, body, requestOptions);

  }

  downloadBookTicket(bookRequest: BookRequest) {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    const body = bookRequest;

    return this.http.post<Blob>(`${this.baseUrl}${JarvisEndpoint.BOOK_TICKET}`, body, requestOptions);

  }


  /** Avvia in Jarvis il book del mese ("YYYY-MM") per tutta l'azienda, caricato come CSV nella cartella del mese su Drive. */
  startBookDriveExport(book: BookDriveName, yearMonth: string) {

    const requestOptions = { headers: this.getHeaders() };

    return this.http.post<BookDriveStartResponse>(`${this.baseUrl}${JarvisEndpoint.BOOK_DRIVE}/${book}/${yearMonth}`, {}, requestOptions);

  }

  getBookDriveExportStatus(book: BookDriveName, yearMonth: string) {

    const requestOptions = { headers: this.getHeaders() };

    return this.http.get<BookDriveStatus>(`${this.baseUrl}${JarvisEndpoint.BOOK_DRIVE}/${book}/${yearMonth}/status`, requestOptions);

  }

  downloadForecastConsuntivoMensileLastMonth() {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.FORECAST_CONSUNTIVO_MENSILE_LAST}`, requestOptions);

  }

  downloadForecastConsuntivoMensileCurrentMonth() {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.FORECAST_CONSUNTIVO_MENSILE_CURRENT}`, requestOptions);

  }

  downloadForecastPrevision() {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.FORECAST_PREVISION}`, requestOptions);

  }

  downloadForecastForProject(projectKey: string) {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.FORECAST_PREVISION_PROJECT}/${projectKey}`, requestOptions);

  }

  downloadForecastForProjectAll() {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.FORECAST_PREVISION_PROJECT_SENIORITY_ALL}`, requestOptions);

  }

  downloadForecastAbsence() {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.FORECAST_ABSENCE}`, requestOptions);

  }

  downloadExpenseClaimPDF(acuplaceId: string, yearMonth: string) {

    const requestOptions = {
      headers: this.getHeaders(),
      responseType: 'blob' as 'json'
    };

    return this.http.get<Blob>(`${this.baseUrl}${JarvisEndpoint.EXPENSE_GENERATE_PDF}/${acuplaceId}/${yearMonth}`, requestOptions);

  }

  getAllUser(): Observable<any[]> {
    return this.http.get<any[]>(
      this.baseUrl.concat(this.endpointUser)
    );
  }

  async getDiagnosticByUser(identifier: string, monthYear: string, forceUpdate: boolean = false): Promise<any> {
    const requestOptions = { headers: this.getHeaders() }; return await this.http.get<any>(
      `${this.baseUrl}/diagnostic/user/${identifier}/${monthYear}?forceUpdate=${forceUpdate}`,
      requestOptions
    ).toPromise();
  }


  ngOnInit(): void {
    console.log("ngOnInit JarvisApiService ")
  }
}
